import { useEffect, useRef, useState } from "react";
import { Send, Keyboard } from "lucide-react";

/**
 * ActionPanel — the primary player input surface.
 *
 * Modern-standards upgrades (iter48):
 *  - Number-key hotkeys (1-4) instantly pick the corresponding choice.
 *  - Slash "/" focuses the custom-action input so power users never touch the mouse.
 *  - `?` toggles a tiny hotkey hint overlay.
 *  - Each choice shows its hotkey chip so the UI teaches itself.
 *  - Pressed-state animation feedback so click/tap always feels responsive.
 */
export default function ActionPanel({ choices = [], onAction, disabled, offlineMode = false }) {
  const [custom, setCustom] = useState("");
  const [pressed, setPressed] = useState(null);    // index of choice being pressed (for feedback pulse)
  const [showHelp, setShowHelp] = useState(false);
  const inputRef = useRef(null);

  const fireChoice = (idx) => {
    const c = choices[idx];
    if (!c || disabled) return;
    setPressed(idx);
    setTimeout(() => setPressed(null), 180);
    onAction({ choice: c });
  };

  const submitCustom = (e) => {
    e?.preventDefault?.();
    const t = custom.trim();
    if (!t || disabled) return;
    onAction({ custom_text: t });
    setCustom("");
  };

  // Global hotkeys. Skip when a text input is focused so typing doesn't pick actions.
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const inField = tag === "input" || tag === "textarea" || e.target?.isContentEditable;

      // `?` toggles help (works anywhere)
      if (e.key === "?" && !inField) {
        setShowHelp((v) => !v);
        e.preventDefault();
        return;
      }
      // `/` focuses custom input
      if (e.key === "/" && !inField) {
        inputRef.current?.focus();
        e.preventDefault();
        return;
      }
      // Number keys 1-4 pick the corresponding choice
      if (!inField && ["1", "2", "3", "4"].includes(e.key)) {
        const idx = parseInt(e.key, 10) - 1;
        if (idx < choices.length) {
          fireChoice(idx);
          e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [choices, disabled]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="panel p-5 relative" data-testid="action-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.4em] ash-text">— YOUR MOVE —</div>
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          data-testid="action-help-toggle"
          className="flex items-center gap-1 text-[9px] tracking-[0.3em] ash-text hover:text-[#f0e6d2] transition-colors"
          title="Toggle hotkey hints (?)"
        >
          <Keyboard size={11} />
          {showHelp ? "HIDE KEYS" : "?"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {choices.map((c, i) => {
          const hotkey = i < 4 ? `${i + 1}` : null;
          const isPressed = pressed === i;
          return (
            <button
              key={`${i}-${c}`}
              disabled={disabled}
              data-testid="action-choice-button"
              onClick={() => fireChoice(i)}
              className={`btn-glow fade-up delay-${(i % 4) + 1} relative text-left transition-all ${isPressed ? "scale-[0.98] opacity-80" : ""}`}
            >
              {hotkey && (
                <span
                  className="absolute top-1.5 right-2 text-[9px] tracking-[0.15em] px-1.5 py-0.5 rounded-sm"
                  data-testid="hotkey-chip"
                  style={{
                    background: "rgba(212,175,55,0.10)",
                    border: "1px solid rgba(212,175,55,0.35)",
                    color: "#d4af37",
                  }}
                >
                  {hotkey}
                </span>
              )}
              <span className="gold-text mr-2">›</span>{c}
            </button>
          );
        })}
      </div>

      {/* iter67.8 — Custom action form removed per user request.
          Players stay on the curated choice path (no more "run out of choices" soft-lock). */}

      {showHelp && (
        <div
          className="mt-4 panel p-3 text-[10px] tracking-[0.2em] ash-text"
          data-testid="action-help-panel"
          style={{ background: "rgba(15,10,5,0.7)", borderColor: "rgba(212,175,55,0.25)" }}
        >
          <div className="grid grid-cols-2 gap-y-1 gap-x-4">
            <div><span className="gold-text">1-4</span> · pick choice</div>
            <div><span className="gold-text">/</span> · custom action</div>
            <div><span className="gold-text">Enter</span> · submit</div>
            <div><span className="gold-text">?</span> · toggle keys</div>
          </div>
        </div>
      )}
    </div>
  );
}
